import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  TableContainer,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify"; // Import ToastContainer
import Pagination from "../components/Pagination.js";

const baseUrl = config.baseURL;

const Trash = () => {
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState("customers");
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [itemsPerPage, setItemsPerPage] = useState(10); // Items per page
  const [totalItems, setTotalItems] = useState(0); // Total number of items
  const [totalPages, setTotalPages] = useState(1);
  const modules = [
    "customers",
    "materials",
    "products",
    "orders",
    "suppliers",
    "complaints",
    "production_batches",
    "earnings",
    "expenses",
    // Add other modules as necessary
  ];

  const navigate = useNavigate();
  useEffect(() => {
    const fetchDeletedItems = async () => {
      setLoading(true);

      try {
        // Fetch deleted items for all modules simultaneously
        const fetchPromises = modules.map((module) =>
          axios.get(`${baseUrl}/api/trashed-items/${module}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          })
        );

        const responses = await Promise.all(fetchPromises);
        console.log("responses", responses);

        // Combine the results from all modules
        const allDeletedItems = responses.flatMap((response) => {
          if (response && response.data.data) {
            return response.data.data;
          }
          return []; // Return an empty array if there's no data
        });
        console.log("allDeletedItems", allDeletedItems);

        // Sort by `deleted_at` in descending order (newest first)
        const sortedItems = allDeletedItems.sort((a, b) => {
          const dateA = new Date(a.deleted_at);
          const dateB = new Date(b.deleted_at);
          return dateB - dateA; // For descending order (newest first)
        });

        // Paginate the items for the current page
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPageItems = sortedItems.slice(startIndex, endIndex);

        setDeletedItems(currentPageItems);
        setTotalItems(sortedItems.length); // Total number of items
        setTotalPages(Math.ceil(sortedItems.length / itemsPerPage)); // Calculate total pages
      } catch (err) {
        console.error("Error fetching trashed items", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeletedItems();
  }, [selectedModule, currentPage, itemsPerPage]);

  const handleRestore = async (id, module) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/restore-item/${module}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      // Check if the response is successful
      if (response && response.data && response.data.message) {
        alert(response.data.message); // Show a message
        // Remove the restored item from the list (or fetch the list again)
        setDeletedItems(deletedItems.filter((item) => item.id !== id));
      }
    } catch (err) {
      console.error("Error restoring item", err);
      alert("Error restoring item");
    }
  };

  const handleDeleteForever = async (id, module) => {
    try {
      const response = await axios.delete(
        `${baseUrl}/api/delete-forever/${module}/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      alert(response.data.message);
      setDeletedItems(deletedItems.filter((item) => item.id !== id)); // Remove deleted item from the list
    } catch (err) {
      console.error("Error deleting item forever", err);
      alert("Error deleting item forever");
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  // Styling variables
  const backgroundColor = "#F4F6F9"; // Background color for the page
  const textColor = "#333333";

  return (
    <div
      className="container mx-auto p-16"
      style={{
        backgroundColor: backgroundColor,
        minHeight: "100vh",
      }}
    >
      <ToastContainer />

      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: "Playfair Display",
            fontWeight: "bold",
          }}
          style={{ color: textColor }}
        >
          Archived / Trash
        </Typography>
      </div>

      {/* Table to display trashed items */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="trashed items table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  backgroundColor: "#2E4053",
                }}
              >
                Module
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  backgroundColor: "#2E4053",
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  backgroundColor: "#2E4053",
                }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  backgroundColor: "#2E4053",
                }}
              >
                Deleted At
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#FFFFFF",
                  backgroundColor: "#2E4053",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deletedItems.map((item) => (
              <TableRow
                key={`${item.module}-${item.id}`}
                style={{ backgroundColor: "#FFFFFF" }}
              >
                <TableCell>{item.module}</TableCell>
                <TableCell>
                  {item.name || item.firstname + " " + item.lastname}
                </TableCell>
                <TableCell>{item.description || "No description"}</TableCell>
                <TableCell>
                  {new Date(item.deleted_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    sx={{
                      marginTop: "10px",
                      color: "#FFFFFF",
                      backgroundColor: "#2E4053",
                      "&:hover": {
                        backgroundColor: "#1A2A3C", // Darker shade for hover
                      },
                    }}
                    onClick={() => handleRestore(item.id, item.module)}
                  >
                    Restore
                  </Button>
                  <Button
                    sx={{
                      marginTop: "10px",
                      marginLeft: "10px",
                      color: "#FFFFFF",
                      backgroundColor: "darkred",
                      "&:hover": {
                        backgroundColor: "#660000", // Darker red for hover state
                      },
                    }}
                    onClick={() => handleDeleteForever(item.id, item.module)}
                  >
                    Delete Forever
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Section */}
      <div
        style={{
          position: "fixed", // Fixed positioning ensures it stays in place
          bottom: "20px",
          left: "50%", // Center the pagination horizontally
          transform: "translateX(-50%)", // Adjust position to perfectly center
          width: "auto", // Auto width for pagination container
          backgroundColor: "transparent", // Set a white background for visibility
          padding: "10px 20px", // Padding around the pagination for spacing
          zIndex: 1000, // Ensure it stays on top of other content
        }}
      >
        <Pagination
          totalItem={totalItems} // Total items for current page
          itemPerPage={itemsPerPage} // Items per page
          currentPage={currentPage} // Current page (1-based index)
          totalPages={totalPages} // Total number of pages
          onPageChange={(newPage) => {
            console.log("Page changed to:", newPage);
            setCurrentPage(newPage); // Update current page
          }}
          fetchItems={(newPage) => {
            setCurrentPage(newPage); // Update the current page
          }} // Function to fetch items (already handled by useEffect)
        />
      </div>
    </div>
  );
};

export default Trash;
